import { t } from '@/i18n'
import { defineStore } from 'pinia'
import { api, json } from '@/services/api'
import type { NextTimeMemory, NextTimeMemoryPlacement, PlaceNextTimeMemoryRequest, TripDetail } from '@/types/api'

type MemorySource = { tripName: string, destination?: string, startDate: string, endDate: string, campingStyleName: string }
type QueuedMemory = { id: string, accountId: string, tripId: string, text: string, createdAt: string, source: MemorySource }
const databaseName = 'camplist-next-time'

function database(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(databaseName, 2)
    request.onupgradeneeded = () => {
      if (request.result.objectStoreNames.contains('memories')) request.result.deleteObjectStore('memories')
      request.result.createObjectStore('memories', { keyPath: 'id' })
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}
async function queued(accountId: string): Promise<QueuedMemory[]> {
  if (!('indexedDB' in window)) return []
  const db = await database(); return await new Promise((resolve, reject) => {
    const request = db.transaction('memories').objectStore('memories').getAll()
    request.onsuccess = () => { db.close(); resolve((request.result as QueuedMemory[]).filter(item => item.accountId === accountId)) }
    request.onerror = () => { db.close(); reject(request.error) }
  })
}
async function put(item: QueuedMemory) { const db = await database(); await new Promise<void>((resolve, reject) => { const request = db.transaction('memories', 'readwrite').objectStore('memories').put(item); request.onsuccess = () => { db.close(); resolve() }; request.onerror = () => { db.close(); reject(request.error) } }) }
async function remove(id: string) { const db = await database(); await new Promise<void>((resolve, reject) => { const request = db.transaction('memories', 'readwrite').objectStore('memories').delete(id); request.onsuccess = () => { db.close(); resolve() }; request.onerror = () => { db.close(); reject(request.error) } }) }
export async function clearNextTimeQueue() { if (!('indexedDB' in window)) return; const db = await database(); await new Promise<void>((resolve, reject) => { const request = db.transaction('memories', 'readwrite').objectStore('memories').clear(); request.onsuccess = () => { db.close(); resolve() }; request.onerror = () => { db.close(); reject(request.error) } }) }

export const useNextTimeStore = defineStore('nextTime', {
  state: () => ({ memories: [] as NextTimeMemory[], outstandingByTrip: {} as Record<string, NextTimeMemory[]>, loading: false, status: '' }),
  getters: {
    forTrip: state => (tripId: string) => state.memories.filter(memory => memory.sourceTripId === tripId && !memory.setAsideAt),
    outstandingForTrip: state => (tripId: string) => state.outstandingByTrip[tripId] ?? []
  },
  actions: {
    async load(accountId?: string, includeSetAside = false) {
      if (!accountId) { this.memories = []; return }
      this.loading = true
      try { this.memories = await api<NextTimeMemory[]>(`/api/next-time/memories${includeSetAside ? '?includeSetAside=true' : ''}`) }
      catch { /* queued notes still remain visible below */ }
      finally {
        const local = await queued(accountId).catch(() => [])
        const pending = local.map(item => ({ id: `pending:${item.id}`, text: item.text, sourceTripId: item.tripId, sourceTripName: item.source.tripName, sourceDestination: item.source.destination, sourceStartDate: item.source.startDate, sourceEndDate: item.source.endDate, sourceCampingStyleName: item.source.campingStyleName, createdAt: item.createdAt }))
        this.memories = [...pending, ...this.memories.filter(memory => !pending.some(item => item.id === memory.id))]
        this.loading = false
      }
    },
    async save(accountId: string, trip: TripDetail, lines: string[]) {
      const source: MemorySource = { tripName: trip.name, destination: trip.destination, startDate: trip.startDate, endDate: trip.endDate, campingStyleName: trip.campingStyle.name }
      const entries = lines.map(text => ({ id: crypto.randomUUID(), accountId, tripId: trip.id, text, createdAt: new Date().toISOString(), source }))
      let storedOffline = false
      for (const entry of entries) {
        try {
          if (!navigator.onLine) throw new TypeError('Offline')
          const memory = await api<NextTimeMemory>(`/api/trips/${trip.id}/next-time/memories`, json('POST', { text: entry.text, clientOperationId: entry.id, source: entry.source }))
          this.memories.unshift(memory)
        } catch (error: any) {
          if (error?.status && error.status < 500) throw error
          await put(entry); this.memories.unshift({ id: `pending:${entry.id}`, text: entry.text, sourceTripId: trip.id, sourceTripName: source.tripName, sourceDestination: source.destination, sourceStartDate: source.startDate, sourceEndDate: source.endDate, sourceCampingStyleName: source.campingStyleName, createdAt: entry.createdAt }); storedOffline = true
        }
      }
      this.status = storedOffline ? t('copy.ade5400f2170') : t('copy.24423b0d908c')
    },
    async flush(accountId?: string) {
      if (!accountId || !navigator.onLine) return
      for (const entry of await queued(accountId).catch(() => [])) {
        try {
          const memory = await api<NextTimeMemory>(`/api/trips/${entry.tripId}/next-time/memories`, json('POST', { text: entry.text, clientOperationId: entry.id, source: entry.source }))
          await remove(entry.id)
          this.memories = [memory, ...this.memories.filter(item => item.id !== `pending:${entry.id}` && item.id !== memory.id)]
        } catch { break }
      }
    },
    async setAside(id: string, setAside: boolean) {
      if (id.startsWith('pending:')) { await remove(id.slice(8)); this.memories = this.memories.filter(item => item.id !== id); return }
      const memory = await api<NextTimeMemory>(`/api/next-time/memories/${id}`, json('PATCH', { setAside }))
      this.memories = this.memories.map(item => item.id === id ? memory : item)
    },
    async loadOutstanding(tripId: string, accountId?: string) {
      if (!accountId) { this.outstandingByTrip[tripId] = []; return [] }
      await this.flush(accountId)
      const memories = await api<NextTimeMemory[]>(`/api/trips/${tripId}/next-time/memories/outstanding`)
      this.outstandingByTrip[tripId] = memories
      return memories
    },
    async place(tripId: string, memoryId: string, request: PlaceNextTimeMemoryRequest) {
      const result = await api<NextTimeMemoryPlacement>(`/api/trips/${tripId}/next-time/memories/${memoryId}/placements`, json('POST', request))
      this.outstandingByTrip[tripId] = (this.outstandingByTrip[tripId] ?? []).filter(item => item.id !== memoryId)
      return result
    },
    clear() { this.memories = []; this.outstandingByTrip = {}; this.status = '' }
  }
})
